/** A named filter set saved by a user (localStorage only). */
export type SavedFilter<T> = {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly filters: T;
};

/**
 * Parses stored saved filters, tolerating corrupt JSON. Each entry's filters go through `normalize`,
 * so filters saved in an older shape still apply.
 */
export const parseStoredSavedFilters = <T>(
  raw: string | null,
  normalize: (stored: never) => T,
): readonly SavedFilter<T>[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Drop malformed entries individually so one bad entry can't wipe the whole list on the next save.
    const entries = parsed.filter((entry): entry is SavedFilter<never> => (
      typeof entry === 'object' && entry !== null && typeof entry.filters === 'object' && entry.filters !== null
    ));
    return entries.map((saved) => ({ ...saved, filters: normalize(saved.filters) }));
  } catch {
    return [];
  }
};
